import identity from 'lodash/identity';
import { useTranslation } from 'react-i18next';

import { TabValue as AssignmentDetailsTabValue } from 'applications/GSP/entities/AssignmentDetails/types';
import { TabValue as SubjectDetailsTabValue } from 'applications/GSP/pages/Subjects/pages/SubjectDetails/types';
import {
  MainRoutePath,
  SubjectsRoutePath,
} from 'applications/GSP/shared/constants/router';
import {
  AllUpcomingAssignmentsRoutePath,
  AssignmentsRoutePath,
  DashboardRoutePath,
  ProgressRoutePath,
} from 'applications/GSP/shared/constants/router/routePaths';

import { useParentHelperRouteConfig } from './hooks';

import type { RouteObject } from 'shared/types';

export const lazy = () =>
  import('shared/components/UnderConstruction').then(
    ({ UnderConstruction }) => ({ Component: UnderConstruction }),
  );

export const useMainRoutesConfig = (): RouteObject[] => {
  const { t } = useTranslation();
  // TODO: implement general solution to show routes according to featureFlags
  const parentHelperRouteConfig = useParentHelperRouteConfig();

  return [
    {
      path: MainRoutePath.Dashboard,
      handle: {
        crumb: t('GSP.layouts.mainLayout.header.breadcrumbs.dashboard'),
      },
      children: [
        {
          index: true,
          lazy: () =>
            import('applications/GSP/pages/Dashboard').then(
              ({ Dashboard }) => ({
                Component: Dashboard,
              }),
            ),
        },
        {
          path: DashboardRoutePath.AllUpcomingAssignments,
          lazy: () =>
            import(
              'applications/GSP/pages/Dashboard/pages/AllUpcomingAssignments'
            ).then(({ AllUpcomingAssignments }) => ({
              Component: AllUpcomingAssignments,
            })),
          children: [
            {
              path: AllUpcomingAssignmentsRoutePath.Assignments,
              handle: {
                crumb: t(
                  'GSP.layouts.mainLayout.header.breadcrumbs.upcomingAssignments',
                ),
              },
              lazy: () =>
                import(
                  'applications/GSP/pages/Dashboard/pages/AllUpcomingAssignments/pages/UpcomingAssignments'
                ).then(({ UpcomingAssignments }) => ({
                  Component: UpcomingAssignments,
                })),
            },
            {
              path: AllUpcomingAssignmentsRoutePath.AssignmentDetails,
              handle: { crumb: identity },
              lazy: () =>
                import(
                  'applications/GSP/pages/Dashboard/pages/AllUpcomingAssignments/pages/UpcomingAssignmentDetails'
                ).then(({ UpcomingAssignmentDetails }) => ({
                  Component: UpcomingAssignmentDetails,
                })),
              children: [
                {
                  index: true,
                  handle: { tabValue: AssignmentDetailsTabValue.Overview },
                  lazy: () =>
                    import(
                      'applications/GSP/entities/AssignmentDetails/pages/Overview'
                    ).then(({ Overview }) => ({
                      Component: Overview,
                    })),
                },
                {
                  path: AssignmentsRoutePath.Results,
                  handle: { tabValue: AssignmentDetailsTabValue.Results },
                  lazy: () =>
                    import(
                      'applications/GSP/entities/AssignmentDetails/pages/Results'
                    ).then(({ Results }) => ({
                      Component: Results,
                    })),
                },
              ],
            },
          ],
        },
      ],
    },
    {
      path: MainRoutePath.Calendar,
      handle: {
        crumb: t('GSP.layouts.mainLayout.header.breadcrumbs.calendar'),
      },
      lazy: () =>
        import('applications/GSP/pages/Calendar').then(({ Calendar }) => ({
          Component: Calendar,
        })),
    },
    {
      path: MainRoutePath.Subjects,
      handle: {
        crumb: t('GSP.layouts.mainLayout.header.breadcrumbs.subjects.crumb'),
      },
      children: [
        {
          index: true,
          lazy: () =>
            import('applications/GSP/pages/Subjects').then(({ Subjects }) => ({
              Component: Subjects,
            })),
        },
        {
          path: SubjectsRoutePath.SubjectDetails,
          handle: { crumb: identity },
          lazy: () =>
            import('applications/GSP/pages/Subjects/pages/SubjectDetails').then(
              ({ SubjectDetails }) => ({
                Component: SubjectDetails,
              }),
            ),
          children: [
            {
              index: true,
              handle: {
                tabValue: SubjectDetailsTabValue.Overview,
                crumb: t(
                  'GSP.layouts.mainLayout.header.breadcrumbs.subjects.overview',
                ),
              },
              lazy: () =>
                import(
                  'applications/GSP/pages/Subjects/pages/SubjectDetails/pages/Overview'
                ).then(({ Overview }) => ({
                  Component: Overview,
                })),
            },
            {
              path: SubjectsRoutePath.Assignments,
              handle: {
                tabValue: SubjectDetailsTabValue.Assignments,
                crumb: t(
                  'GSP.layouts.mainLayout.header.breadcrumbs.subjects.assignments',
                ),
              },
              lazy: () =>
                import(
                  'applications/GSP/pages/Subjects/pages/SubjectDetails/pages/Assignments'
                ).then(({ Assignments }) => ({
                  Component: Assignments,
                })),
            },
            {
              path: SubjectsRoutePath.AssignmentDetails,
              handle: {
                tabValue: SubjectDetailsTabValue.Assignments,
                crumb: identity,
              },
              lazy: () =>
                import(
                  'applications/GSP/pages/Subjects/pages/SubjectDetails/pages/AssignmentDetails'
                ).then(({ AssignmentDetails }) => ({
                  Component: AssignmentDetails,
                })),
              children: [
                {
                  index: true,
                  handle: { tabValue: AssignmentDetailsTabValue.Overview },
                  lazy: () =>
                    import(
                      'applications/GSP/entities/AssignmentDetails/pages/Overview'
                    ).then(({ Overview }) => ({
                      Component: Overview,
                    })),
                },
                {
                  path: AssignmentsRoutePath.Results,
                  handle: { tabValue: AssignmentDetailsTabValue.Results },
                  lazy: () =>
                    import(
                      'applications/GSP/entities/AssignmentDetails/pages/Results'
                    ).then(({ Results }) => ({
                      Component: Results,
                    })),
                },
              ],
            },
            {
              path: SubjectsRoutePath.Resources,
              handle: {
                tabValue: SubjectDetailsTabValue.Resources,
                crumb: t(
                  'GSP.layouts.mainLayout.header.breadcrumbs.subjects.resources',
                ),
              },
              lazy: () =>
                import(
                  'applications/GSP/pages/Subjects/pages/SubjectDetails/pages/Resources'
                ).then(({ Resources }) => ({
                  Component: Resources,
                })),
            },
            {
              path: SubjectsRoutePath.Syllabus,
              handle: {
                tabValue: SubjectDetailsTabValue.Syllabus,
                crumb: t(
                  'GSP.layouts.mainLayout.header.breadcrumbs.subjects.syllabus',
                ),
              },
              lazy: () =>
                import(
                  'applications/GSP/pages/Subjects/pages/SubjectDetails/pages/Syllabus'
                ).then(({ Syllabus }) => ({
                  Component: Syllabus,
                })),
            },
          ],
        },
      ],
    },
    {
      path: MainRoutePath.Progress,
      handle: {
        crumb: t('GSP.layouts.mainLayout.header.breadcrumbs.progress.crumb'),
      },
      lazy: () =>
        import('applications/GSP/pages/Progress').then(({ Progress }) => ({
          Component: Progress,
        })),
      children: [
        {
          index: true,
          handle: {
            crumb: t(
              'GSP.layouts.mainLayout.header.breadcrumbs.progress.overview',
            ),
          },
          lazy: () =>
            import('applications/GSP/pages/Progress/pages/Overview').then(
              ({ Overview }) => ({
                Component: Overview,
              }),
            ),
        },
        {
          path: ProgressRoutePath.Subject,
          handle: { crumb: identity },
          lazy: () =>
            import('applications/GSP/pages/Progress/pages/Subject').then(
              ({ Subject }) => ({
                Component: Subject,
              }),
            ),
        },
      ],
    },
    ...parentHelperRouteConfig,
    {
      path: MainRoutePath.SchoolLinks,
      handle: {
        crumb: t('GSP.layouts.mainLayout.header.breadcrumbs.schoolLinks'),
      },
      lazy: () =>
        import('applications/GSP/pages/SchoolLinks').then(
          ({ SchoolLinks }) => ({
            Component: SchoolLinks,
          }),
        ),
    },
  ];
};
